import ApiHelper from "../../utils/apiHelper";
import { checkVersion } from "../../utils/helpers/checkVersion";
import { getPlatformType, PlatformType } from "../../utils/helpers/subdomainHelpers/getPlatformType";

export const globalActionTypes = {
  SET_INITIAL_CONFIG: "SET_INITIAL_CONFIG",
  SET_CURRENT_APP_VERSION: "SET_CURRENT_APP_VERSION",
  SET_VERSION_STATUS: "SET_VERSION_STATUS",
  TOGGLE_GLOBAL_LOADER: "TOGGLE_GLOBAL_LOADER",
  TOGGLE_LANGUAGE_MENU_DISABLE: "TOGGLE_LANGUAGE_MENU_DISABLE",
  SET_PLATFORM_TYPE: "SET_PLATFORM_TYPE",
  SET_APP_ERROR: "SET_APP_ERROR",
  CLEAR_APP_ERROR: "CLEAR_APP_ERROR"
};

export interface AppError {
  active: boolean;
  status: number | null;
  goToMainDomain: boolean;
}

export const initialState = {
  apiUrl: null,
  transferApiUrl: null,
  defaultDomain: null,
  newRelicBrowserEnabled: null,
  newRelicBrowserApplicationId: null,
  newRelicBrowserLicenseKey: null,
  newRelicBrowserAccountId: null,
  newRelicBrowserTrustKey: null,
  newRelicBrowserAgentId: null,
  newRelicBrowserBeacon: null,
  currentAppVersion: null,
  versionUpdateStatus: { status: false, version: "" },
  globalLoader: true,
  isLanguageMenuDisabled: false,
  platformType: getPlatformType(window.location.hostname),
  soketiAppKey: null,
  soketiWsHost: null,
  gtmAuth: null,
  gtmPreview: null,
  appError: { active: false, status: null, goToMainDomain: false }
};

interface globalModuleTypes {
  apiUrl: string | null;
  transferApiUrl: string | null;
  defaultDomain: string | null;
  newRelicBrowserEnabled?: string | null;
  newRelicBrowserApplicationId?: string | null;
  newRelicBrowserLicenseKey?: string | null;
  newRelicBrowserAccountId?: string | null;
  newRelicBrowserTrustKey?: string | null;
  newRelicBrowserAgentId?: string | null;
  newRelicBrowserBeacon?: string | null;
  currentAppVersion: string | null;
  globalLoader: boolean;
  isLanguageMenuDisabled: boolean;
  platformType: PlatformType;
  soketiAppKey: string | null;
  soketiWsHost: string | null;
  gtmAuth?: string | null;
  gtmPreview?: string | null;
  appError: AppError;
}

const globalModule = (state: globalModuleTypes = initialState, action: any) => {
  switch (action.type) {
    case globalActionTypes.TOGGLE_GLOBAL_LOADER:
      return {
        ...state,
        globalLoader: action.globalLoader
      };
    case globalActionTypes.SET_INITIAL_CONFIG:
      return {
        ...state,
        apiUrl: action.appConfig.api_url,
        transferApiUrl: action.appConfig.transfer_api_url,
        defaultDomain: action.appConfig.default_domain,
        googleMapApiKey: action.appConfig.google_map_api_key,
        gtmContainerId: action.appConfig.gtm_container_id,
        gtmAuth: action.appConfig.gtm_auth,
        gtmPreview: action.appConfig.gtm_preview,
        googlePlacesApiKey: action.appConfig.google_places_api_key,
        soketiAppKey: action.appConfig.soketi_app_key,
        soketiWsHost: action.appConfig.soketi_ws_host,
        newRelicBrowserEnabled: action.appConfig.new_relic_browser_enabled,
        newRelicBrowserApplicationId: action.appConfig.new_relic_browser_application_id,
        newRelicBrowserLicenseKey: action.appConfig.new_relic_browser_license_key,
        newRelicBrowserAccountId: action.appConfig.new_relic_browser_account_id,
        newRelicBrowserTrustKey: action.appConfig.new_relic_browser_trust_key,
        newRelicBrowserAgentId: action.appConfig.new_relic_browser_agent_id,
        newRelicBrowserBeacon: action.appConfig.new_relic_browser_beacon
      };
    case globalActionTypes.SET_CURRENT_APP_VERSION:
      return {
        ...state,
        currentAppVersion: action.currentAppVersion
      };
    case globalActionTypes.SET_VERSION_STATUS:
      return {
        ...state,
        versionUpdateStatus: action.versionUpdateStatus
      };
    case globalActionTypes.TOGGLE_LANGUAGE_MENU_DISABLE:
      return {
        ...state,
        isLanguageMenuDisabled: action.isLanguageMenuDisabled
      };
    case globalActionTypes.SET_PLATFORM_TYPE:
      return {
        ...state,
        platformType: action.platformType
      };
    case globalActionTypes.SET_APP_ERROR:
      return {
        ...state,
        appError: { active: true, status: action.status, goToMainDomain: action.goToMainDomain }
      };
    case globalActionTypes.CLEAR_APP_ERROR:
      return {
        ...state,
        appError: { active: false, status: null, goToMainDomain: false }
      };

    default:
      return state;
  }
};

export default globalModule;

// helper to select the correct api url based on the platform type
export const selectApiBaseUrl = (state: { global: globalModuleTypes }) => {
  const { apiUrl, transferApiUrl, platformType } = state.global;

  if (platformType === "airline" && transferApiUrl) {
    return transferApiUrl;
  }

  return apiUrl;
};

export const selectAppError = (state: { global: globalModuleTypes }): AppError => state.global.appError;

export const selectNewRelicBrowserConfig = (state: { global: globalModuleTypes }) => {
  const {
    newRelicBrowserEnabled,
    newRelicBrowserApplicationId,
    newRelicBrowserLicenseKey,
    newRelicBrowserAccountId,
    newRelicBrowserTrustKey,
    newRelicBrowserAgentId,
    newRelicBrowserBeacon
  } = state.global;

  return {
    enabled: newRelicBrowserEnabled,
    applicationId: newRelicBrowserApplicationId,
    licenseKey: newRelicBrowserLicenseKey,
    accountId: newRelicBrowserAccountId,
    trustKey: newRelicBrowserTrustKey,
    agentId: newRelicBrowserAgentId,
    beacon: newRelicBrowserBeacon
  };
};

export const globalActionCreators = {
  toggleGlobalLoaderAction: (globalLoader: boolean) => {
    return {
      type: globalActionTypes.TOGGLE_GLOBAL_LOADER,
      globalLoader
    };
  },
  toggleLanguageMenuDisableAction: (isLanguageMenuDisabled: boolean) => {
    return {
      type: globalActionTypes.TOGGLE_LANGUAGE_MENU_DISABLE,
      isLanguageMenuDisabled
    };
  },
  setPlatformTypeAction: (platformType: PlatformType) => {
    return {
      type: globalActionTypes.SET_PLATFORM_TYPE,
      platformType
    };
  },
  setAppErrorAction: ({ status, goToMainDomain = false }: { status: number | null; goToMainDomain?: boolean }) => {
    return {
      type: globalActionTypes.SET_APP_ERROR,
      status,
      goToMainDomain
    };
  },
  clearAppErrorAction: () => {
    return {
      type: globalActionTypes.CLEAR_APP_ERROR
    };
  },
  getConfigAction: () => async (dispatch: (arg0: { type: string; appConfig: any }) => void) => {
    try {
      const response = await ApiHelper.get(`${window.location.origin}/config.json`);

      dispatch({
        type: globalActionTypes.SET_INITIAL_CONFIG,
        appConfig: response.data
      });
    } catch (err) {
      return {};
    }
  },
  getCurrentAppVersionAction: () => async (dispatch: (arg0: { type: string; currentAppVersion: any }) => void) => {
    try {
      // Dynamically generate a timestamp for cache-busting
      const timestamp = new Date().getTime();

      const response = await ApiHelper.get(`${window.location.origin}/version.json?v=${timestamp}`);

      dispatch({
        type: globalActionTypes.SET_CURRENT_APP_VERSION,
        currentAppVersion: response.data?.tag
      });
    } catch (err) {
      return {};
    }
  },
  checkAppVersionStatusAction:
    () => async (dispatch: (arg0: { type: string; versionUpdateStatus: any }) => void, getState: any) => {
      try {
        const currentAppVersion = getState().global.currentAppVersion;
        // Dynamically generate a timestamp for cache-busting
        const timestamp = new Date().getTime();

        const response = await ApiHelper.get(`${window.location.origin}/version.json?v=${timestamp}`);
        const versionCheck = checkVersion(currentAppVersion?.substring(1), response?.data?.tag?.substring(1));

        dispatch({
          type: globalActionTypes.SET_VERSION_STATUS,
          versionUpdateStatus: versionCheck
        });
      } catch (err) {
        return {};
      }
    }
};
